"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { stegaClean } from "@sanity/client/stega";

import { cn } from "@/lib/utils";
import type { PageRizAffiliateSchema } from "@/sanity/pages/PageRizAffiliateTemplate";

type Chip = NonNullable<PageRizAffiliateSchema["heroChips"]>[number];

const SPOTS = [
  { left: "71.4063%", top: "44.4302%" },
  { left: "50%", top: "61.0755%" },
  { left: "69.8958%", top: "77.3367%" },
];

const ICONS: Record<string, ReactNode> = {
  microphone: (
    <>
      <path d="M12.9375 7.875V9.5625C12.9375 10.6068 12.5227 11.6083 11.7842 12.3467C11.0458 13.0852 10.0443 13.5 9 13.5C7.95571 13.5 6.95419 13.0852 6.21577 12.3467C5.47734 11.6083 5.0625 10.6068 5.0625 9.5625V7.875H3.9375V9.5625C3.93809 10.8073 4.39732 12.0083 5.22744 12.936C6.05755 13.8636 7.20036 14.4529 8.4375 14.5913V15.75H6.1875V16.875H11.8125V15.75H9.5625V14.5913C10.7996 14.4529 11.9424 13.8636 12.7726 12.936C13.6027 12.0083 14.0619 10.8073 14.0625 9.5625V7.875H12.9375Z" />
      <path d="M9 12.375C9.74592 12.375 10.4613 12.0787 10.9887 11.5512C11.5162 11.0238 11.8125 10.3084 11.8125 9.5625V3.9375C11.8125 3.19158 11.5162 2.47621 10.9887 1.94876C10.4613 1.42132 9.74592 1.125 9 1.125C8.25408 1.125 7.53871 1.42132 7.01126 1.94876C6.48382 2.47621 6.1875 3.19158 6.1875 3.9375V9.5625C6.1875 10.3084 6.48382 11.0238 7.01126 11.5512C7.53871 12.0787 8.25408 12.375 9 12.375ZM7.3125 3.9375C7.3125 3.48995 7.49029 3.06072 7.80676 2.74426C8.12322 2.42779 8.55245 2.25 9 2.25C9.44755 2.25 9.87678 2.42779 10.1932 2.74426C10.5097 3.06072 10.6875 3.48995 10.6875 3.9375V9.5625C10.6875 10.0101 10.5097 10.4393 10.1932 10.7557C9.87678 11.0722 9.44755 11.25 9 11.25C8.55245 11.25 8.12322 11.0722 7.80676 10.7557C7.49029 10.4393 7.3125 10.0101 7.3125 9.5625V3.9375Z" />
    </>
  ),
  headphones: (
    <path d="M14.0625 9V8.4375C14.0625 7.09484 13.5291 5.80717 12.5797 4.85777C11.6303 3.90837 10.3427 3.375 9 3.375C7.65734 3.375 6.36967 3.90837 5.42027 4.85777C4.47087 5.80717 3.9375 7.09484 3.9375 8.4375V9C3.19158 9 2.47621 9.29632 1.94876 9.82376C1.42132 10.3512 1.125 11.0666 1.125 11.8125C1.125 12.5584 1.42132 13.2738 1.94876 13.8012C2.47621 14.3287 3.19158 14.625 3.9375 14.625H5.0625V8.4375C5.0625 7.39321 5.47734 6.39169 6.21577 5.65327C6.95419 4.91484 7.95571 4.5 9 4.5C10.0443 4.5 11.0458 4.91484 11.7842 5.65327C12.5227 6.39169 12.9375 7.39321 12.9375 8.4375V14.625H14.0625C14.8084 14.625 15.5238 14.3287 16.0512 13.8012C16.5787 13.2738 16.875 12.5584 16.875 11.8125C16.875 11.0666 16.5787 10.3512 16.0512 9.82376C15.5238 9.29632 14.8084 9 14.0625 9ZM2.25 11.8125C2.25 11.3649 2.42779 10.9357 2.74426 10.6193C3.06072 10.3028 3.48995 10.125 3.9375 10.125V13.5C3.48995 13.5 3.06072 13.3222 2.74426 13.0057C2.42779 12.6893 2.25 12.2601 2.25 11.8125ZM14.0625 13.5V10.125C14.5101 10.125 14.9393 10.3028 15.2557 10.6193C15.5722 10.9357 15.75 11.3649 15.75 11.8125C15.75 12.2601 15.5722 12.6893 15.2557 13.0057C14.9393 13.3222 14.5101 13.5 14.0625 13.5Z" />
  ),
  youtube: (
    <path d="M16.5431 5.20878C16.4535 4.8758 16.278 4.5722 16.0341 4.32837C15.7903 4.08454 15.4867 3.90903 15.1537 3.8194C13.9275 3.48753 8.99999 3.48753 8.99999 3.48753C8.99999 3.48753 4.07249 3.48753 2.84624 3.8194C2.51326 3.90903 2.20966 4.08454 1.96583 4.32837C1.722 4.5722 1.54649 4.8758 1.45686 5.20878C1.2279 6.45936 1.11678 7.72868 1.12499 9.00003C1.11678 10.2714 1.2279 11.5407 1.45686 12.7913C1.54649 13.1243 1.722 13.4279 1.96583 13.6717C2.20966 13.9155 2.51326 14.091 2.84624 14.1807C4.07249 14.5125 8.99999 14.5125 8.99999 14.5125C8.99999 14.5125 13.9275 14.5125 15.1537 14.1807C15.4867 14.091 15.7903 13.9155 16.0341 13.6717C16.278 13.4279 16.4535 13.1243 16.5431 12.7913C16.7721 11.5407 16.8832 10.2714 16.875 9.00003C16.8832 7.72868 16.7721 6.45936 16.5431 5.20878ZM7.42499 11.3625V6.63753L11.5144 9.00003L7.42499 11.3625Z" />
  ),
};

const ICON_COLORS: Record<string, string> = {
  microphone: "#409E80",
  headphones: "#409E80",
  youtube: "#FF0000",
};

export default function HeroChips({ chips, className }: { chips?: Chip[]; className?: string }) {
  const refs = useRef<Array<HTMLDivElement | null>>([]);
  const items = (chips ?? []).filter((chip) => chip?.label || chip?.value).slice(0, SPOTS.length);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const nodes = refs.current.slice(0, items.length);
    const start = performance.now();
    let frame = 0;
    let visible = true;
    const tick = (now: number) => {
      const t = (now - start) / 1000;
      nodes.forEach((node, i) => {
        if (!node) return;
        const phase = i * 1.7;
        const amp = (4 + (i % 3) * 1.5) / 8;
        const speed = 0.5 + (i % 4) * 0.09;
        const y = Math.sin(t * speed + phase) * amp;
        const x = Math.cos(t * speed * 0.7 + phase) * amp * 0.6;
        node.style.translate = `${x.toFixed(3)}rem ${y.toFixed(3)}rem`;
      });
      frame = visible ? requestAnimationFrame(tick) : 0;
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !frame) frame = requestAnimationFrame(tick);
    });
    if (nodes[0]?.parentElement) io.observe(nodes[0].parentElement);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      io.disconnect();
    };
  }, [items.length]);

  if (!items.length) return null;

  return (
    <div className={className}>
      {items.map((chip, i) => {
        const icon = stegaClean(chip.icon) ?? "";
        return (
          <div
            key={i}
            ref={(node) => {
              refs.current[i] = node;
            }}
            className="lg:absolute"
            style={SPOTS[i]}>
            <div
              data-intro="chip"
              className="flex h-[5.75rem] w-fit items-center gap-[1rem] rounded-[0.75rem] border border-white/[0.12] bg-[#0B1512]/[0.44] py-[0.75rem] pl-[0.75rem] pr-[2rem] backdrop-blur-[0.9625rem]">
              {ICONS[icon] && (
                <span className="flex h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-[0.5rem] bg-white">
                  <svg
                    viewBox="0 0 18 18"
                    aria-hidden
                    fill={ICON_COLORS[icon]}
                    className="h-[2.25rem] w-[2.25rem]">
                    {ICONS[icon]}
                  </svg>
                </span>
              )}
              <p className={cn("m-0 whitespace-nowrap font-geist text-[1.75rem] font-[500] leading-[2.5rem] text-white", !ICONS[icon] && "pl-[1.25rem]")}>
                {chip.label && <span className="mr-[0.9375rem] text-white/[0.65]">{chip.label}</span>}
                {chip.value}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
